import { useAuth } from "@dutyon/apis";
import {
  Box,
  Button,
  Container,
  modals, // Assuming modals is exported from @dutyon/ui-kit
  Paper,
  PasswordInput,
  Text,
  TextInput,
  Title,
} from "@dutyon/ui-kit";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

export function EmailSignUpPage() {
  const navigate = useNavigate();
  const { signUpWithEmail } = useAuth(); // removed loading from useAuth, using mutation state

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting: isFormSubmitting },
  } = useForm<EmailSignUpFormValues>({
    defaultValues: {
      email: "",
      password: "",
      name: "",
    },
  });

  const { mutate: signUp, isPending } = useMutation({
    mutationFn: (data: EmailSignUpFormValues) => signUpWithEmail(data),
    onSuccess: () => {
      modals.openConfirmModal({
        title: "회원가입 완료",
        children: (
          <Text size="sm">
            회원가입이 완료되었습니다.
            <br />
            로그인 페이지로 이동합니다.
          </Text>
        ),
        labels: { confirm: "확인", cancel: "취소" },
        confirmProps: { color: "blue" },
        onConfirm: () => navigate("/login"),
        onCancel: () => navigate("/login"),
        withCloseButton: false,
        closeOnCancel: false,
        closeOnConfirm: false,
      });
    },
    onError: (error: unknown) => {
      const errorCode =
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        typeof error.code === "string"
          ? error.code
          : undefined;
      if (errorCode === "auth/email-already-in-use") {
        setError("email", {
          message: "이미 사용 중인 이메일입니다.",
        });
      } else {
        setError("root", {
          message: "회원가입에 실패했습니다. 다시 시도해주세요.",
        });
      }
    },
  });

  const onSubmit = (data: EmailSignUpFormValues) => {
    signUp(data);
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f8f9fa",
      }}
    >
      <Container size={420} p={0}>
        <Title ta="center" order={1} mb="md" style={{ color: "#111827" }}>
          DutyOn Backoffice
        </Title>
        <Text c="dimmed" size="sm" ta="center" mb={30}>
          이메일로 회원가입하세요
        </Text>

        <Paper
          withBorder
          shadow="xl"
          py={30}
          px={52}
          miw={420}
          radius="md"
          style={{ backgroundColor: "white" }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextInput
              label="이름 (선택)"
              placeholder="홍길동"
              {...register("name")}
              error={errors.name?.message}
            />
            <TextInput
              label="Email"
              placeholder="admin@dutyon.com"
              required
              mt="md"
              {...register("email", {
                required: "이메일을 입력해주세요",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "유효한 이메일 주소를 입력해주세요",
                },
              })}
              error={errors.email?.message}
            />
            <PasswordInput
              label="Password"
              placeholder="Your password"
              required
              mt="md"
              {...register("password", {
                required: "비밀번호를 입력해주세요",
                minLength: {
                  value: 6,
                  message: "비밀번호는 6자 이상이어야 합니다",
                },
              })}
              error={errors.password?.message}
            />
            {errors.root && (
              <div style={{ color: "#e03131", marginTop: 10, fontSize: 14 }}>
                {errors.root.message}
              </div>
            )}
            <Button
              fullWidth
              mt="xl"
              size="md"
              type="submit"
              loading={isFormSubmitting || isPending}
            >
              회원가입
            </Button>

            <Button fullWidth mt="sm" size="md" variant="subtle" onClick={() => navigate("/login")}>
              로그인으로 돌아가기
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}

interface EmailSignUpFormValues {
  email: string;
  password: string;
  name: string;
}
